import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, X, XCircle, Send } from "lucide-react";
import { authService } from "@b2b/services";
import { Button, BizcoverIndiaBrandMark } from "@b2b/ui-components";
import FormInput from "@b2b/ui-components/features/Registration/components/FormInput";

interface Toast {
  type: "error" | "success";
  message: string;
}

function getServerErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
    name?: string;
  };
  const raw = e.response?.data?.message;
  if (raw != null) {
    return Array.isArray(raw) ? raw.join(" ") : String(raw);
  }
  if (e.name === "ApiError" && typeof e.message === "string" && e.message) {
    return e.message;
  }
  return undefined;
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledEmail = (location.state as { email?: string } | null)?.email ?? "";

  const [email, setEmail] = useState(prefilledEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setToast({ type: "error", message: "Please enter your email address." });
      return;
    }
    setIsLoading(true);
    try {
      await authService.requestForgotPassword(trimmed);
      setSubmitted(true);
      setToast(null);
    } catch (error: unknown) {
      setToast({
        type: "error",
        message: getServerErrorMessage(error) || "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#f9fafb]">
      <header className="absolute left-6 top-6 z-10 flex flex-col gap-1 md:left-8 md:top-8">
        <BizcoverIndiaBrandMark variant="auth" to="/bizcoverindia-home-updated" />
        <p className="hidden text-xs text-gray-600 sm:block">Your trusted B2B marketplace</p>
      </header>

      <div className="flex min-h-screen flex-col items-center justify-center overflow-y-auto px-4 py-10 pt-24 sm:px-6 md:px-8 md:py-12 md:pt-12">
        <div className="mx-auto flex w-full min-w-0 max-w-md flex-col">
          <div className="w-full min-w-0 shrink-0 rounded-2xl border border-[#EEF0F3] bg-white p-4 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)] transition-shadow sm:p-6 md:p-10">
            <div className="mb-6 flex rounded-xl bg-[#f3f4f6] p-1">
              <Link
                to="/login"
                className="flex flex-1 items-center justify-center rounded-lg py-2.5 text-sm font-bold text-gray-600 transition-all hover:text-gray-900"
                role="tab"
              >
                Sign In
              </Link>
              <span
                className="flex flex-1 cursor-default items-center justify-center rounded-lg bg-white py-2.5 text-sm font-bold text-[#f15a11] shadow-sm"
                aria-selected={true}
                role="tab"
              >
                Forgot password
              </span>
            </div>

            {toast && (
              <div
                className={`mb-6 flex items-center gap-2 rounded-lg p-4 ${
                  toast.type === "error"
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-green-200 bg-green-50 text-green-800"
                }`}
              >
                <XCircle
                  className={`h-5 w-5 flex-shrink-0 ${toast.type === "error" ? "text-red-600" : "text-green-600"}`}
                />
                <p className="flex-1 text-sm font-medium">{toast.message}</p>
                <button
                  type="button"
                  onClick={() => setToast(null)}
                  className="ml-2 flex-shrink-0 transition-opacity hover:opacity-80"
                  aria-label="Close notification"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {!submitted ? (
              <div className="space-y-6 transition-opacity">
                <p className="text-center text-sm text-gray-600">
                  Enter the email you use to sign in. If you have a password on file, we will send you a reset
                  link.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <FormInput
                      id="forgot-email"
                      label="Email"
                      icon={Mail}
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      autoFocus
                      visualVariant="authSignin"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="ctaPrimary"
                    size="lg"
                    className="h-12 w-full rounded-xl bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Sending…
                      </div>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send reset link
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-600">
                  New to Bizcover?{" "}
                  <Link
                    to="/login?tab=signup"
                    className="font-bold text-[#f15a11] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 focus-visible:ring-offset-2 rounded-sm"
                  >
                    Create an account
                  </Link>
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-gray-700">
                  If an account exists for that email, we sent instructions to reset your password. Check
                  your inbox and spam folder. The link expires in one hour.
                </p>
                <Button
                  type="button"
                  variant="ctaPrimary"
                  size="lg"
                  className="h-12 w-full rounded-xl bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
                  onClick={() => navigate("/login", { replace: true })}
                >
                  Return to sign in
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
